import {
  Button,
  Card,
  Chip,
  CircularProgress,
  Skeleton,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material';
import { Box } from '@mui/system';
import { Settings, Warning, CheckCircle } from '@mui/icons-material';
import ContractTitleSection from './ContractTitleSection';
import ContractTitleAvatars from './ContractTitleAvatars';
import dayjs from 'dayjs';
import { useState } from 'react';
import ContractEditDialog from './ContractEditDialog';
import ContractStatusPopover from './ContractStatusPopover';

export default function ContractTitle({
  contract,
  party,
  isLoading,
  coverageStatus,
  totalContractVolume,
  onContractUpdate,
}) {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const handleSaveContractDetails = (updatedContractData) => {
    onContractUpdate(updatedContractData);
    setIsEditDialogOpen(false);
  };

  return (
    <>
      <Card sx={{ width: '100%', height: 'fit-content', p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {isLoading ? (
            <Skeleton variant="rounded" width={60} height={24} />
          ) : (
            <Chip label="Aktiv" color="success" sx={{ mb: 1 }} />
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {coverageStatus?.status === 'loading' ? (
              <CircularProgress size={24} />
            ) : coverageStatus?.status === 'warning' ? (
              <ContractStatusPopover
                title="Abdeckungsstatus"
                messages={coverageStatus.messages}
              >
                <Warning color="warning" />
              </ContractStatusPopover>
            ) : (
              <Tooltip title="Alle Positionen der Quelldaten sind Verträgen zugeordnet.">
                <CheckCircle color="success" />
              </Tooltip>
            )}

            <IconButton
              size="small"
              onClick={() => setIsEditDialogOpen(true)}
              disabled={isLoading || !contract}
            >
              <Settings color={isLoading ? 'disabled' : 'action'} />
            </IconButton>
          </Box>
        </Box>
        <Typography variant="h5" sx={{ mt: 1, fontWeight: 'bold' }}>
          {isLoading ? (
            <Skeleton width="60%" />
          ) : (
            contract?.title || 'Unbekannter Vertrag'
          )}
        </Typography>
        <Typography sx={{ mt: 1, color: 'text.secondary' }}>
          {isLoading ? (
            <Skeleton width="90%" />
          ) : (
            contract?.description || 'Keine Beschreibung verfügbar.'
          )}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 2,
            mt: 2,
            width: '100%',
          }}
        >
          <ContractTitleSection
            title="Kunde"
            isLoading={isLoading}
            data={`${contract?.partyName ?? 'Unbekannt'} ${party?.customerNumber ? `(${party.customerNumber})` : ''}`}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 2,
            mt: 2,
            width: '100%',
          }}
        >
          <ContractTitleSection
            title="Vertragsnummer"
            isLoading={isLoading}
            data={contract?.contractNumber}
          />
          <ContractTitleSection
            title="Vertragsdatum"
            isLoading={isLoading}
            data={dayjs(contract?.contractDate).format('DD.MM.YYYY')}
          />
          <ContractTitleSection
            title="Letzte Abrechnung"
            isLoading={isLoading}
            data={
              contract?.lastInvoicedAt
                ? `${dayjs(contract?.lastInvoicedAt).format('DD.MM.YYYY HH:mm')} Uhr`
                : '–'
            }
          />
          <ContractTitleSection
            title="Vertragsvolumen"
            isLoading={isLoading}
            data={totalContractVolume.toLocaleString('de-DE', {
              style: 'currency',
              currency: 'EUR',
            })}
          />
          <ContractTitleSection
            title="Abrechnung"
            isLoading={isLoading}
            data={
              contract?.billingFrequency === 'no_billing'
                ? 'Keine Abrechnung'
                : contract?.billingFrequency
            }
          />
        </Box>

        <ContractTitleSection
          title="Produkte"
          isLoading={isLoading}
          data={
            <ContractTitleAvatars
              products={contract?.products || []}
              isLoading={isLoading}
            />
          }
          mt={2}
        />
      </Card>

      {isEditDialogOpen && (
        <ContractEditDialog
          open={isEditDialogOpen}
          onClose={() => setIsEditDialogOpen(false)}
          data={contract}
          onSave={handleSaveContractDetails}
        />
      )}
    </>
  );
}
